import React from "react";
import { Helmet } from "react-helmet";

const BASE_URL = "https://yoyokhel.com";
const DEFAULT_IMAGE = `${BASE_URL}/logo556.png`;
const SITE_NAME = "Yoyo Game";

/**
 * Reusable SEO head component.
 *
 * Props:
 *  title        – page title (will be suffixed with " • Yoyo Game")
 *  description  – meta description (max ~160 chars)
 *  keywords     – comma-separated keyword string
 *  image        – absolute URL to OG image (defaults to site logo)
 *  url          – canonical URL for this page
 *  type         – og:type (default: "website")
 *  noIndex      – boolean, set true for private/auth pages
 *  jsonLd       – plain object or array of objects to inject as JSON-LD
 */
const SEOHead = ({
  title,
  description,
  keywords,
  image = DEFAULT_IMAGE,
  url,
  type = "website",
  noIndex = false,
  jsonLd,
}) => {
  const fullTitle = title ? `${title} • ${SITE_NAME}` : `${SITE_NAME} • Download & Play Games on PC, Mobile, Console & VR`;
  const canonicalUrl = url ? url : BASE_URL;

  return (
    <Helmet>
      {/* Basic */}
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {/* JSON-LD structured data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd])}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
